/**
 * AffiliatedPeople Component
 * Displays a list of users who have self-affiliated with a specific organization.
 * Shown on the organization detail page (OrganizationDetail) to help visitors
 * see who is connected to the organization.
 *
 * Key behaviors:
 * - Fetches affiliated users from GET /api/organizations/:id/affiliated on mount
 * - Renders nothing (returns null) if no affiliated users exist
 * - Shows each user's name, profile picture (with initial-letter fallback), and
 *   affiliation type badge (professional, alumni, volunteer, collaborator, interested)
 * - This is a read-only display component; affiliation management happens on the
 *   user's profile page via the AffiliationManager component
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, ListGroup, Badge, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { useOrganizationApi } from '@/shared/services/organizationApi';

// Maps affiliation type strings to Bootstrap badge color variants.
// These types correspond to the affiliation options presented in AffiliationManager.
const TYPE_BADGE_COLORS = {
    professional: 'primary',
    alumni: 'secondary',
    volunteer: 'success',
    collaborator: 'info',
    interested: 'warning',
};

export default function AffiliatedPeople({ organizationId }) {
    const [affiliatedUsers, setAffiliatedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Tracks broken profile picture URLs by user._id so we can show
    // an initial-letter avatar fallback instead of a broken image icon.
    const [imageErrors, setImageErrors] = useState({});

    const orgApi = useOrganizationApi();

    // Fetch the list of users affiliated with this organization.
    // The API returns user profile data plus their affiliation type
    // (e.g., { name, profilePicture, affiliation: { type: 'professional' } }).
    const fetchAffiliatedUsers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await orgApi.fetchAffiliatedUsers(organizationId);
            setAffiliatedUsers(data.affiliatedUsers || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching affiliated users:', err);
            setError('Failed to load affiliated people');
        } finally {
            setLoading(false);
        }
    }, [orgApi, organizationId]);

    // Fetch on mount and whenever organizationId changes
    useEffect(() => {
        fetchAffiliatedUsers();
    }, [fetchAffiliatedUsers]);

    // Render nothing if the fetch SUCCEEDED but there are no affiliated users
    // -- the entire card is hidden rather than showing an empty state, keeping
    // the org detail page clean for organizations without affiliations yet.
    // A load ERROR must NOT be swallowed this way: when `error` is set we fall
    // through and render the card so the failure surfaces to the visitor
    // instead of the card silently disappearing.
    if (!loading && !error && affiliatedUsers.length === 0) {
        return null;
    }

    return (
        <Card className="border-0 shadow-sm mt-4">
            <Card.Body className="p-4">
                <h5 className="text-primary mb-3">
                    People Connected to This Organization
                </h5>

                {loading && (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                    </div>
                )}

                {error && <p className="text-danger small">{error}</p>}

                {/* User list: each row shows a profile picture (or initial-letter
                    fallback), the user's full name, and a colored badge for their
                    affiliation type. The badge color is looked up from TYPE_BADGE_COLORS. */}
                {!loading && affiliatedUsers.length > 0 && (
                    <ListGroup variant="flush">
                        {affiliatedUsers.map((user) => (
                            <ListGroup.Item
                                key={user._id}
                                className="d-flex align-items-center justify-content-between px-0"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    {/* Profile picture with broken-image fallback */}
                                    {user.profilePicture &&
                                    !imageErrors[user._id] ? (
                                        <img
                                            src={user.profilePicture}
                                            alt={`${user.name?.first} ${user.name?.last}`}
                                            className="profile-thumbnail-xs rounded-circle"
                                            onError={() =>
                                                setImageErrors((prev) => ({
                                                    ...prev,
                                                    [user._id]: true,
                                                }))
                                            }
                                        />
                                    ) : (
                                        <div className="profile-placeholder-xs rounded-circle bg-primary d-flex align-items-center justify-content-center text-white small">
                                            {user.name?.first?.[0] || '?'}
                                        </div>
                                    )}
                                    <span>
                                        {user.name?.first} {user.name?.last}
                                    </span>
                                </div>
                                <Badge
                                    bg={
                                        TYPE_BADGE_COLORS[
                                            user.affiliation?.type
                                        ] || 'secondary'
                                    }
                                >
                                    {user.affiliation?.type}
                                </Badge>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Card.Body>
        </Card>
    );
}

AffiliatedPeople.propTypes = {
    organizationId: PropTypes.string.isRequired,
};
